<script lang="ts">
  import type { UnitType } from "api/site_maps"
  import { USDateFormatter } from "./utils"
  import { getContext, onDestroy } from "svelte"
  import { numberToCurrency } from "utils"
  import Icon from "components/Icon.svelte"

  export let storageUnit: UnitType & { id: string; name: string; dimensions: string }

  const customerPopoverStore = getContext("customerPopoverStore")
  const defaultRect = () => ({ height: 0, width: 0 })
  const rootElementId = `customer-popover-${storageUnit.id}`

  let bottomOffset: Number = 0
  let containerRect: DOMRect
  let customerPopoverStoreUnsusbscribe = () => {}
  let rightOffset: Number = 0
  let rootElementRect: DOMRect = defaultRect()
  let selectedUnitId: string
  let selectedUnitRect: DOMRect
  let sitemapStatusesUnsubscribe = () => {}

  customerPopoverStoreUnsusbscribe = customerPopoverStore.subscribe((current) => {
    containerRect = current.containerRect
    selectedUnitId = current.id
    selectedUnitRect = current.storageUnitRect
  })

  function buildHeader() {
    if (storageUnit.auction) {
      let label = "In Auction"

      if (storageUnit.auctionDate) {
        const formattedDate = USDateFormatter.format(new Date(storageUnit.auctionDate))
        label = `Auction Date: ${formattedDate}`
      }

      return {
        classNames: "bg-yellow-300 border-yellow-500 text-slate-700",
        label,
      }
    }

    if (storageUnit.scheduledMoveIn) {
      const formattedDate = USDateFormatter.format(new Date(storageUnit.scheduledMoveIn))

      return {
        classNames: "bg-blue-500 border-blue-700 text-white",
        label: `Scheduled Move In: ${formattedDate}`,
      }
    }

    if (storageUnit.scheduledMoveOut) {
      const formattedDate = USDateFormatter.format(new Date(storageUnit.scheduledMoveOut))

      return {
        classNames: "bg-purple-600 border-purple-800 text-white",
        label: `Scheduled Move Out: ${formattedDate}`,
      }
    }

    if (storageUnit.disabled) {
      return {
        classNames: "bg-gray-900/80 border-black text-white",
        label: "Disabled",
      }
    }

    if (storageUnit.occupancyStatus == "occupied") {
      return {
        classNames: "bg-emerald-500 border-emerald-600 text-white",
        label: "Occupied",
      }
    }

    if (storageUnit.occupancyStatus == "past_due") {
      return {
        classNames: "bg-red-500 border-red-600 text-white",
        label: "Past Due",
      }
    }

    if (storageUnit.occupancyStatus == "vacant") {
      return {
        classNames: "bg-slate-100 border-slate-200 text-slate-700",
        label: "Vacant",
      }
    }

    return null
  }

  function getBalanceClass() {
    if (storageUnit.occupancyStatus == "past_due") {
      return "text-red-500"
    }

    if (storageUnit.customer.balance <= 0) {
      return "text-green-600"
    }

    return ""
  }

  function getLeftOffset($a_, $b_) {
    return selectedUnitRect.left + selectedUnitRect.width - containerRect.left
  }

  function getTopOffset($a_, $b_) {
    return selectedUnitRect.top + selectedUnitRect.height - containerRect.top
  }

  function handleOpenChange(open) {
    if (!open) {
      return
    }

    requestAnimationFrame(() => {
      const rootElement = document.getElementById(rootElementId)

      if (!rootElement) {
        return
      }

      rootElementRect = rootElement.getBoundingClientRect()

      const xBleedOver = containerRect.width - (getLeftOffset() + rootElementRect.width)
      const isXBleedingOver = xBleedOver < 0

      rightOffset = isXBleedingOver ? rootElementRect.width + selectedUnitRect.width : 0

      const yBleedOver = containerRect.height - (getTopOffset() + rootElementRect.height)
      const isYBleedingOver = yBleedOver < 0

      bottomOffset = isYBleedingOver ? rootElementRect.height + selectedUnitRect.height : 0
    })
  }

  const header = buildHeader()

  $: leftOffset = getLeftOffset(containerRect, selectedUnitRect) - rightOffset
  $: topOffset = getTopOffset(containerRect, selectedUnitRect) - bottomOffset

  $: open = storageUnit.id === selectedUnitId

  $: {
    handleOpenChange(open)
  }

  onDestroy(() => {
    customerPopoverStoreUnsusbscribe()
    sitemapStatusesUnsubscribe()
  })
</script>

<div
  class={`${open ? "absolute" : "hidden"} bg-white overflow-hidden rounded-xl shadow-lg`}
  id={rootElementId}
  style={`left: ${leftOffset}px; top: ${topOffset}px; width: 300px;`}
>
  <div class="flex flex-col h-full w-full">
    {#if header}
      <header
        class={`border-b flex font-bold items-center justify-center p-3 text-sm ${header.classNames}`}
      >
        {header.label}
      </header>
    {/if}

    <div>
      <div>
        {#if storageUnit.customer}
          <p
            class="bg-slate-100 flex font-bold gap-2 items-center justify-center p-3 text-sm text-slate-700"
          >
            <Icon class="h-4 shrink-0 w-4" name="user" />
            <span>{storageUnit.customer.name}</span>
          </p>
        {/if}

        <div class="divide-y">
          <div class="border-t grid grid-cols-2 divide-x items-stretch justify-between">
            <div class="flex gap-2 p-2">
              <Icon class="h-4 shrink-0 w-4" name="cube-o" />

              <p class="flex flex-col">
                <span class="font-bold text-xs text-slate-700">
                  {storageUnit.name}
                </span>

                <span class="text-[0.6rem] text-slate-500">
                  {storageUnit.dimensions}
                </span>
              </p>
            </div>

            <div class="flex gap-2 p-2">
              <Icon class="h-4 shrink-0 w-4" name="currency-dollar-o" />

              <p class="flex flex-col">
                <span class="font-bold text-xs text-slate-700">
                  ${storageUnit.price}
                </span>

                <span class="text-[0.6rem] text-slate-500">
                  {storageUnit.priceDuration || "Monthly"}
                </span>
              </p>
            </div>
          </div>

          {#if storageUnit.lockCode}
            <div class="flex gap-2 p-2">
              <Icon class="h-4 shrink-0 w-4" name="lock-closed-o" />

              <p class="flex flex-col">
                <span class="font-bold text-xs text-slate-700">Lock Code</span>

                <span class="text-[0.6rem] text-slate-500">
                  {storageUnit.lockCode}
                </span>
              </p>
            </div>
          {/if}
        </div>
      </div>
    </div>

    {#if storageUnit.disabled}
      <div
        class="bg-slate-100 border-t border-slate-200 flex flex-col flex-grow items-center justify-center p-3 text-gray-700"
      >
        <span class="font-normal italic text-xs">This unit cannot be leased</span>
      </div>
    {:else if storageUnit.customer}
      <div class="flex items-start">
        <hr class="border-slate-200 w-full" />
        <p
          class="border border-slate-200 flex items-center font-bold gap-1 p-2 rounded-b-md text-xs"
        >
          <Icon class="h-4 w-4" name="scale" />
          <span>Balance</span>
        </p>
        <hr class="border-slate-200 w-full" />
      </div>

      <div class="flex flex-col flex-grow gap-2 items-center justify-center p-3">
        <p class={`text-center text-lg font-bold ${getBalanceClass()}`}>
          {numberToCurrency(storageUnit.customer.balance)}
        </p>
      </div>
    {/if}

    {#if storageUnit.locked}
      <footer class="flex justify-center">
        <p
          class="bg-cyan-500 border border-cyan-600 border-b-0 flex font-bold gap-2 items-center p-2 rounded-t-md text-xs text-white"
        >
          <Icon class="h-4 shrink-0 w-4" name="lock-closed" />
          <span>Locked</span>
        </p>
      </footer>
    {/if}
  </div>
</div>
